/**
 * Line + Bar chart — mixed per-series geometry on shared (and optionally dual)
 * axes.
 *
 * Unlike line/bar (one geometry for the whole chart), this type reads each
 * `series[].type` ('bar' | 'line' | 'area') and renders that series with its
 * own geometry, so bars and lines coexist: e.g. metal as grouped bars on the
 * left axis + grade as a line on the right axis (`series[].yAxis`). The
 * secondary axis is data-driven by the shared layout (any series with
 * `yAxis: 'right'` turns it on); bar mode is this type's own `buildLayout`
 * override (grouped by default, `stack` when `options.stacked`) — like the bar
 * chart.
 *
 * It is series-driven: with no `series[]` it draws nothing (there is no
 * single-`yKey` fallback — a lone value has no geometry to mix).
 */

import type { ChartTypeDefinition } from '../types';
import { registerChart } from '../registry';
import { makeLineBarTraces } from '../shared/traces';
import { xColumn } from '../shared/encoding';
import { figureScale } from '../theme/scale';

export const lineBarChart: ChartTypeDefinition = {
  id: 'lineBar',
  label: 'Line + Bar',
  capabilities: {
    multiSeries: true,
    supportsXKey: true,
    supportsYKey: false,
    supportsGroupBy: false,
    supportsLimits: true,
    filterable: true,
    // Not offered: this type exists to COMPARE measures side by side (tonnage
    // bars against a grade line), and stacking those bars would add one model
    // onto the other — two measurements of the same thing, not parts of a total.
    // `buildLayout` still honours `options.stacked` for a config that sets it
    // deliberately; what this drops is the editor toggle.
    supportsStacked: false,
    legendByDefault: true,
    supportsSecondaryYAxis: true,
    supportsLineStyle: true,
    supportsDataLabels: true,
  },
  buildTraces(model, ctx) {
    const { data, encoding, series, options } = model;
    if (series.length === 0) return [];
    return makeLineBarTraces(
      data,
      xColumn(data, encoding.xKey),
      series,
      ctx.palette,
      options?.showDataLabels,
      figureScale(ctx.figureWidth)
    );
  },
  buildLayout(model) {
    return { barmode: model.options?.stacked ? 'stack' : 'group' };
  },
};

registerChart(lineBarChart);
